import { For, Match, Show, Switch, createSignal, on, createEffect, onMount } from "solid-js";
import { useApi } from "../../api/provider.tsx";
import { describeError, type ApiError } from "../../api/result.ts";
import { CoverageBar } from "../../components/CoverageBar.tsx";
import { LanguagePicker } from "../../components/LanguagePicker.tsx";
import { Button } from "../../components/Form.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createDebouncer } from "../../lib/async.ts";
import type { DraftAnswer, FillView } from "../../api/domain.ts";
import { questionnaireStatus } from "../../api/domain.ts";
import {
  deadOrFailed,
  draftFor,
  productOf,
  progressOf,
  terminalCopy,
  upsertDraft,
  type DeadReason,
  type FillState,
  type SaveState,
} from "./model.ts";
import { QuestionCard } from "./QuestionCard.tsx";
import styles from "./FillPage.module.scss";

const idleSave: SaveState = { state: "idle" };

/** Typing saves once the recipient pauses, not once per keystroke. */
const autosaveDelay = 400;

type TerminalProps = {
  reason: DeadReason;
};

const Terminal = (props: TerminalProps) => {
  const i18n = useI18n();
  const copy = () => terminalCopy(i18n.t(), props.reason);

  return (
    <section class={styles.terminal}>
      <h1>{copy().title}</h1>
      <p>{copy().body}</p>
      <LanguagePicker />
    </section>
  );
};

type FailureProps = {
  error: ApiError;
  onRetry: () => void;
};

const Failure = (props: FailureProps) => {
  const i18n = useI18n();

  return (
    <section class={styles.terminal}>
      <h1>{i18n.t().fill.loadFailed}</h1>
      <p>{describeError(i18n.t(), props.error)}</p>
      <Button label={i18n.t().common.retry} tone="primary" size="large" onClick={props.onRetry} />
    </section>
  );
};

type FillFormProps = {
  view: FillView;
  product: string;
  submitting: boolean;
  saveStateOf: (question: string) => SaveState;
  onChange: (question: string, patch: Partial<DraftAnswer>) => void;
  onSubmit: () => void;
};

const FillForm = (props: FillFormProps) => {
  const i18n = useI18n();
  /** The questionnaire fixed the product, so this only fails on a broken view. */
  const named = () => props.product !== "";

  return (
    <div class={styles.page}>
      <aside class={styles.rail} aria-label={i18n.t().fill.progressTitle}>
        <span class={styles.railTitle}>{i18n.t().fill.progressTitle}</span>
        <CoverageBar {...progressOf(props.view, props.product)} />
        <Show when={named()}>
          <span class={styles.railCount}>
            {i18n.t().fill.product}: {props.view.products[0]?.name}
          </span>
        </Show>
      </aside>

      <main class={styles.main}>
        <a class={styles.skipLink} href="#questions">
          {i18n.t().fill.skipToQuestions}
        </a>
        <header class={styles.header}>
          <div class={styles.headerTop}>
            <h1>{i18n.t().fill.title}</h1>
            <LanguagePicker />
          </div>
          <p class={styles.intro}>
            {i18n.t().fill.greeting} {props.view.author}.
          </p>
          <Show when={!named()}>
            <p class={styles.intro} role="status">
              {i18n.t().fill.productMissing}
            </p>
          </Show>
        </header>

        <section class={styles.group} id="questions" tabindex="-1">
          <Show
            when={props.view.questions.length > 0}
            fallback={<p class={styles.intro}>{i18n.t().fill.noQuestions}</p>}
          >
            <For each={props.view.questions}>
              {(question) => (
                <QuestionCard
                  question={question}
                  draft={draftFor(props.view.drafts, props.product, question.id)}
                  saveState={props.saveStateOf(question.id)}
                  disabled={!named()}
                  onChange={(patch) => props.onChange(question.id, patch)}
                />
              )}
            </For>
          </Show>
        </section>

        <footer class={styles.footer}>
          <Button
            label={props.submitting ? i18n.t().fill.submitting : i18n.t().fill.submit}
            tone="primary"
            size="large"
            disabled={props.submitting || !named() || props.view.questions.length === 0}
            onClick={props.onSubmit}
          />
          <span class={styles.status}>{i18n.t().fill.submitHint}</span>
        </footer>
      </main>
    </div>
  );
};

export type FillPageProps = {
  /** The magic-link token; the route reads it from the URL and passes it in. */
  token: string;
};

/** Public magic-link page: no session, autosave per answer, one product at a time. */
export const FillPage = (props: FillPageProps) => {
  const api = useApi();
  const i18n = useI18n();
  const token = () => props.token;

  const [page, setPage] = createSignal<FillState>({ state: "loading" });
  const [product, setProduct] = createSignal("");
  const [saves, setSaves] = createSignal<Record<string, SaveState>>({});
  const [submitting, setSubmitting] = createSignal(false);
  const autosaves = createDebouncer<DraftAnswer>(autosaveDelay, (draft) => autosave(draft));

  const load = async () => {
    setPage({ state: "loading" });
    const result = await api.fill(token(), { lang: i18n.locale() });
    if (!result.ok) return setPage(deadOrFailed(result.error));

    const view = result.value;
    if (view.status === "answered" || view.status === "evaluated") {
      return setPage({ state: "dead", reason: "submitted" });
    }
    if (product() === "") setProduct(productOf(view.products));
    return setPage({ state: "open", view });
  };

  onMount(() => void load());
  createEffect(on(i18n.locale, () => void load(), { defer: true }));

  const opened = (): FillView | undefined => {
    const current = page();
    return current.state === "open" ? current.view : undefined;
  };

  const deadReason = (): DeadReason | undefined => {
    const current = page();
    return current.state === "dead" ? current.reason : undefined;
  };

  const failure = (): ApiError | undefined => {
    const current = page();
    return current.state === "failed" ? current.error : undefined;
  };

  const saveStateOf = (question: string): SaveState => saves()[question] ?? idleSave;

  const autosave = async (draft: DraftAnswer) => {
    setSaves({ ...saves(), [draft.question]: { state: "saving" } });
    const result = await api.saveDraft(token(), draft.question, {
      value: draft.value,
      comment: draft.comment,
      explanation: draft.explanation,
    });
    setSaves({
      ...saves(),
      [draft.question]: result.ok ? { state: "saved" } : { state: "failed", error: result.error },
    });
  };

  const change = (question: string, patch: Partial<DraftAnswer>) => {
    const view = opened();
    const chosen = product();
    if (view === undefined || chosen === "") return;

    const draft: DraftAnswer = {
      ...(draftFor(view.drafts, chosen, question) ?? {
        questionnaire: view.questionnaire.id,
        product: chosen,
        question,
      }),
      ...patch,
    };

    setPage({ state: "open", view: { ...view, drafts: upsertDraft(view.drafts, draft) } });
    autosaves.push(question, draft);
  };

  const submit = async () => {
    setSubmitting(true);
    /** A pending keystroke must reach the server before the answers are sealed. */
    await autosaves.flush();
    const result = await api.submitFill(token());
    setSubmitting(false);
    if (!result.ok) return setPage(deadOrFailed(result.error));
    return setPage({
      state: "dead",
      reason: questionnaireStatus(result.value) === "pending" ? "unavailable" : "submitted",
    });
  };

  return (
    <Switch fallback={<p class={styles.terminal}>{i18n.t().common.loading}</p>}>
      <Match when={failure()}>
        {(error) => <Failure error={error()} onRetry={() => void load()} />}
      </Match>
      <Match when={deadReason()}>{(reason) => <Terminal reason={reason()} />}</Match>
      <Match when={opened()}>
        {(view) => (
          <FillForm
            view={view()}
            product={product()}
            submitting={submitting()}
            saveStateOf={saveStateOf}
            onChange={change}
            onSubmit={() => void submit()}
          />
        )}
      </Match>
    </Switch>
  );
};
